import type { Vec3 } from '../../geo/types';
import type { MastConfig } from './shipTemplates';
import { WATERLINE, deckAt, mastHeadAt, tierDeckAt, topAt, type FormedTemplate } from './shipForm';
import { BOWSPRIT, MAST, STAFF_HEIGHT } from './rigParts';

/*
 * Where a formed ship's masts, tops and bowsprit stand (docs/plans/ships.md 3). Mastheads keep
 * the heights the painter scale was fitted to; round tops stand at 0.8 of the masthead's height
 * above the water (R3 0.82, R34 0.70; woodcut), topmasts rise from them on the main and fore
 * masts that have one (woodcut).
 */

/** A top's height above the water, as a share of its masthead's. */
export const TOP_SHARE = 0.8;
/** A round top's depth, and its foot's radius as a share of its rim's: the woodcut's flared tops. */
export const TOP = { depth: 0.45, flare: 0.7, sides: 10 } as const;
/** A topmast's radius at its heel, its taper, and how far its heel reaches down past the top. */
export const TOPMAST = { radius: 0.17, taper: 0.6, doubling: 1.2 } as const;
/** How far the lower masthead stands above a top that carries a topmast. */
const MASTHEAD_ABOVE_TOP = 0.6;
/* Shrouds of a bare pole reach up to just under its head, well clear of a lateen sling below. */
const BARE_SHROUD_HEAD = 0.6;
const SHROUD_UNDER_TOP = 0.05;
/** The bowsprit passes this far over the stem head's rail. */
const STEM_CLEAR = 0.25;

export interface MastTop {
  /** Height of its rim in the ship's frame. */
  readonly y: number;
  readonly radius: number;
}

export interface FormedMast {
  readonly config: MastConfig;
  readonly x: number;
  /** Its heel, stepped below the waist deck. */
  readonly heel: number;
  readonly head: number;
  /** Where the lower mast ends: just above its top when a topmast stands on it, else the head. */
  readonly lowerHead: number;
  /** The height of its top, fitted or not: its shrouds and course yard are set by it. */
  readonly topLevel: number;
  readonly top: MastTop | null;
}

export interface Bowsprit {
  readonly heel: Vec3;
  readonly tip: Vec3;
  /** Where the forestay's foot lies on the bowsprit's axis. */
  readonly stay: Vec3;
}

function formedMast(template: FormedTemplate, config: MastConfig): FormedMast {
  const head = mastHeadAt(template, config);
  const topLevel = WATERLINE + TOP_SHARE * (head - WATERLINE);
  return {
    config,
    x: (config.position - 0.5) * template.length,
    heel: deckAt(template, config.position) - MAST.step,
    head,
    lowerHead: config.topmast ? topLevel + MASTHEAD_ABOVE_TOP : head,
    topLevel,
    top: config.top === undefined ? null : { y: topLevel, radius: config.top },
  };
}

export function formedMasts(template: FormedTemplate): readonly FormedMast[] {
  return template.masts.map((config) => formedMast(template, config));
}

/** The radius of a lower mast at height `y`: it tapers from its heel to its head. */
export function lowerRadiusAt(mast: FormedMast, y: number): number {
  const along = (y - mast.heel) / (mast.lowerHead - mast.heel);
  return MAST.radius * (1 - (1 - MAST.taper) * along);
}

/** The radius of a topmast at height `y`, from its heel down in the top to the masthead. */
export function topmastRadiusAt(mast: FormedMast, y: number): number {
  const heel = mast.topLevel - TOPMAST.doubling;
  const along = (y - heel) / (mast.head - heel);
  return TOPMAST.radius * (1 - (1 - TOPMAST.taper) * along);
}

/** The radius of whichever spar stands on the mast's axis at height `y`. */
export function mastRadiusAt(mast: FormedMast, y: number): number {
  return y > mast.lowerHead ? topmastRadiusAt(mast, y) : lowerRadiusAt(mast, y);
}

/** Where the shrouds meet the mast: under the top, or just under a bare pole's head. */
export function shroudHeadY(mast: FormedMast): number {
  return mast.top ? mast.top.y - TOP.depth - SHROUD_UNDER_TOP : mast.head - BARE_SHROUD_HEAD;
}

export function formedStaffTops(template: FormedTemplate): readonly Vec3[] {
  return formedMasts(template)
    .filter((mast) => mast.config.pennant !== false)
    .map((mast): Vec3 => [mast.x, mast.head + STAFF_HEIGHT, 0]);
}

/** The foremost mast, which the forestay and the main stay come down to. */
export function foreMast(masts: readonly FormedMast[]): FormedMast {
  return masts.reduce((a, b) => (b.x < a.x ? b : a));
}

/** The tallest mast, which the main stay and main topmast stay lead forward from. */
export function mainMast(masts: readonly FormedMast[]): FormedMast {
  return masts.reduce((a, b) => (b.head > a.head ? b : a));
}

/* The heel lies on the forecastle, just inside the stem, low enough that the bowsprit passes
   over the stem head's rail with room to spare. */
export function formedBowsprit(template: FormedTemplate): Bowsprit {
  const { heelInset, rise, reach, stay } = BOWSPRIT;
  const heelY = topAt(template, 0) + STEM_CLEAR - heelInset * Math.tan(rise);
  const heel: Vec3 = [-template.length / 2 + heelInset, heelY, 0];
  const along = (t: number): Vec3 => [
    heel[0] - t * reach * template.length * Math.cos(rise),
    heel[1] + t * reach * template.length * Math.sin(rise),
    0,
  ];
  return { heel, tip: along(1), stay: along(stay) };
}

/** The forecastle deck under the bowsprit's heel, where the mid and near tiers step its bitt. */
export function bowspritBittFoot(template: FormedTemplate): number {
  return tierDeckAt(template, template.form.forecastle, BOWSPRIT.heelInset / template.length);
}
